import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Sparkles } from "lucide-react";
import Reveal from "./Reveal";
import { useBooking } from "./BookingDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_VISUALS = {
  "Therapeutic Massages": {
    img: "https://images.unsplash.com/photo-1700522924565-9fad1c05469e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxtYXNzYWdlJTIwdGhlcmFwaXN0JTIwbmV1dHJhbHxlbnwwfHx8fDE3NzgyODc2MjJ8MA&ixlib=rb-4.1.0&q=85",
    accent: "bg-[#EAD584]/20",
  },
  "Rejuvenating Facials": {
    img: "https://images.unsplash.com/photo-1677682692989-0e54aa104350?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxtYXNzYWdlJTIwZmFjaWFsJTIwYWVzdGhldGljaWFuJTIwdHJlYXRtZW50fGVufDB8fHx8MTc3ODI4NzYxMHww&ixlib=rb-4.1.0&q=85",
    accent: "bg-[#1B3370]",
  },
};

const FALLBACK = [
  {
    name: "Therapeutic Massages",
    tagline: "Release tension. Restore balance.",
    items: [
      { name: "60 Minute Massage", price: "$90", duration: "60 min", description: "A grounding session to release tension and bring the body back to balance." },
      { name: "90 Minute Massage", price: "$135", duration: "90 min", description: "Extended bodywork for deeper restoration — ideal for chronic tension." },
      { name: "The Long Restoration — 120 Minute Massage", price: "$180", duration: "120 min", description: "Two full hours of unhurried, therapeutic bodywork." },
    ],
  },
  {
    name: "Rejuvenating Facials",
    tagline: "Now open — a new chapter for the studio.",
    is_new: true,
    items: [
      { name: "The Signature Glow Facial", price: "$100", duration: "60 min", description: "A 60-minute custom facial calibrated to your skin." },
      { name: "The Grand Ritual Facial", price: "$145", duration: "90 min", description: "Ninety minutes of extended skin care — the full slow ritual." },
      { name: "The Renewal Peel", price: "$110", duration: "60 min", description: "A professional chemical peel tailored to your skin's tolerance and goals." },
      { name: "The Velvet Dermaplane", price: "$110", duration: "90 min", description: "A 90-minute dermaplaning treatment for luminous, camera-ready skin." },
    ],
    add_ons: [
      { name: "Diamond Polish (Microdermabrasion)", price: "+$25", description: "A diamond-tip resurfacing pass. Added to any Signature or Grand Ritual facial." },
      { name: "Golden Hands Ritual (hand & arm scrub)", price: "+$10", description: "A warm sugar scrub with massage for hands and forearms." },
      { name: "Petal Lip Ritual (lip scrub)", price: "+$10", description: "A gentle exfoliating polish to soften the lips." },
    ],
  },
];

export default function Services() {
  const [categories, setCategories] = useState(FALLBACK);
  const { openBooking } = useBooking();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/services`);
        if (!cancel && Array.isArray(res.data) && res.data.length) {
          setCategories(res.data);
        }
      } catch (e) {
        console.warn("Services API unavailable, using fallback");
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-[#0F1F4E] text-[#F8F1D8] overflow-hidden grain"
    >
      <div className="pointer-events-none absolute -top-40 right-0 w-[40rem] h-[40rem] rounded-full bg-[#EAD584] blur-3xl opacity-10" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24 items-end">
          <div className="col-span-12 lg:col-span-7">
            <Reveal className="overline-gold mb-6">The menu</Reveal>
            <Reveal delay={1}>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1] font-light text-[#F8F1D8]">
                Treatments shaped <br />
                around <span className="italic text-[#EAD584]">you.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={2} className="col-span-12 lg:col-span-5 lg:pb-3">
            <p className="text-[#D6D6BD] text-base sm:text-lg leading-relaxed max-w-md font-light">
              A tight, intentional menu — therapeutic massage and custom
              facials for the moments that matter. Every booking is built
              for the person on the table.
            </p>
          </Reveal>
        </div>

        <div className="space-y-20 md:space-y-28">
          {categories.map((cat, ci) => {
            const visual = CATEGORY_VISUALS[cat.name] || CATEGORY_VISUALS["Rejuvenating Facials"];
            const reverse = ci % 2 === 1;
            return (
              <div
                key={cat.name}
                id={cat.name === "Rejuvenating Facials" ? "facials" : undefined}
                data-testid={`service-category-${cat.name.toLowerCase().replace(/\s/g, "-")}`}
                className="grid grid-cols-12 gap-6 md:gap-12 items-center scroll-mt-24"
              >
                <Reveal
                  className={`col-span-12 md:col-span-5 ${reverse ? "md:order-2" : ""}`}
                >
                  <div className="relative">
                    <div
                      className={`absolute -inset-3 md:-inset-5 rounded-lg ${visual.accent} -z-10`}
                    />
                    {cat.is_new && (
                      <span
                        data-testid={`service-new-badge-${cat.name.toLowerCase().replace(/\s/g, "-")}`}
                        className="absolute -top-3 -left-3 z-10 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EAD584] text-[#0F1F4E] text-[10px] uppercase tracking-[0.2em] font-medium shadow-md"
                      >
                        <Sparkles className="w-3 h-3" /> New
                      </span>
                    )}
                    <img
                      src={visual.img}
                      alt={`${cat.name} — Sacramento esthetician services at Beauty & Balance`}
                      loading="lazy"
                      className="w-full h-[360px] md:h-[480px] object-cover rounded-lg border border-[#EAD584]/30"
                    />
                  </div>
                </Reveal>

                <Reveal
                  delay={1}
                  className={`col-span-12 md:col-span-7 ${reverse ? "md:order-1" : ""}`}
                >
                  <div className="overline-gold mb-4">0{ci + 1} &mdash; {cat.tagline}</div>
                  <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-[#F8F1D8] mb-8 font-light">
                    {cat.name}
                  </h3>
                  <ul className="divide-y divide-[#EAD584]/25 border-t border-b border-[#EAD584]/25">
                    {cat.items.map((it) => (
                      <li
                        key={it.name}
                        data-testid={`service-item-${it.name.toLowerCase().replace(/\s/g, "-")}`}
                        className="py-5 flex items-start justify-between gap-6 group"
                      >
                        <div className="flex-1">
                          <div className="flex flex-wrap items-baseline gap-3">
                            <span className="font-serif text-xl md:text-2xl text-[#F8F1D8]">
                              {it.name}
                            </span>
                            {it.duration && (
                              <span className="overline-gold opacity-80">{it.duration}</span>
                            )}
                          </div>
                          {it.description && (
                            <p className="mt-2 text-[#D6D6BD] text-sm leading-relaxed font-light max-w-xl">
                              {it.description}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-serif text-2xl md:text-3xl text-[#EAD584]">
                            {it.price}
                          </span>
                          <button
                            type="button"
                            onClick={() => openBooking(it.name)}
                            data-testid={`book-${it.name.toLowerCase().replace(/\s/g, "-")}`}
                            aria-label={`Book ${it.name}`}
                            className="hidden md:inline-flex w-10 h-10 rounded-full border border-[#EAD584] text-[#EAD584] items-center justify-center hover:bg-[#EAD584] hover:text-[#0F1F4E] transition-colors"
                          >
                            <ArrowUpRight className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {cat.add_ons && cat.add_ons.length > 0 && (
                    <div
                      data-testid={`service-addons-${cat.name.toLowerCase().replace(/\s/g, "-")}`}
                      className="mt-8 rounded-lg border border-[#EAD584]/30 bg-[#0F1F4E]/60 p-5 md:p-6"
                    >
                      <div className="overline-gold mb-3">Ritual enhancements</div>
                      <ul className="divide-y divide-[#EAD584]/15">
                        {cat.add_ons.map((ad) => (
                          <li
                            key={ad.name}
                            data-testid={`addon-item-${ad.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            className="py-3 flex items-start justify-between gap-6"
                          >
                            <div className="flex-1">
                              <div className="font-serif text-lg text-[#F8F1D8]">{ad.name}</div>
                              {ad.description && (
                                <p className="mt-1 text-[#D6D6BD] text-xs leading-relaxed font-light max-w-xl">
                                  {ad.description}
                                </p>
                              )}
                            </div>
                            <span className="font-serif text-lg text-[#EAD584] shrink-0">
                              {ad.price}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-3 text-[10px] uppercase tracking-wider text-[#D6D6BD]/70">
                        Mention any add-on in your booking note.
                      </p>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => openBooking()}
                    data-testid={`book-category-${cat.name.toLowerCase().replace(/\s/g, "-")}`}
                    className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm tracking-wider uppercase text-[#EAD584] border border-[#EAD584] hover:bg-[#EAD584] hover:text-[#0F1F4E] transition-colors"
                  >
                    Book {cat.name.split(" ").pop()} <ArrowUpRight className="w-4 h-4" />
                  </button>
                </Reveal>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
